/**
 * Personalization API Service - интеграция с backend Personalization endpoints
 * Event Master v1.5 - Backend Integration
 * Новые возможности: метрики производительности и временные паттерны
 */

import { httpClient } from '../http-client.service';
import { buildQueryString } from './query-builder';
import { ApiResponse } from '../../types/responses/api-response';
import {
  UserPerformanceMetrics,
  CreatePerformanceMetricsDto,
  GetPerformanceMetricsRequest,
  CalculatePerformanceMetricsRequest,
  PerformanceMetricsStatisticsResponse,
  TemporalPattern,
  TemporalPatternAnalysisDto,
  CreateTemporalPatternDto,
  AdaptPreferencesRequest,
  TemporalPatternRecommendationsResponse,
} from '../../types/personalization';

const BASE_URL = '/personalization';

// ===== МЕТРИКИ ПРОИЗВОДИТЕЛЬНОСТИ =====

/**
 * Получение метрик производительности пользователя
 * GET /api/personalization/performance-metrics
 */
export const getPerformanceMetrics = async (
  params?: GetPerformanceMetricsRequest
): Promise<UserPerformanceMetrics[]> => {
  const { data } = await httpClient.get<ApiResponse<UserPerformanceMetrics[]>>(
    `${BASE_URL}/performance-metrics${buildQueryString(params || {})}`
  );

  return data.data;
};

/**
 * Создание метрики производительности
 * POST /api/personalization/performance-metrics
 */
export const createPerformanceMetrics = async (
  payload: CreatePerformanceMetricsDto
): Promise<UserPerformanceMetrics> => {
  const { data } = await httpClient.post<ApiResponse<UserPerformanceMetrics>>(
    `${BASE_URL}/performance-metrics`,
    payload
  );

  return data.data;
};

/**
 * Расчет метрик производительности на основе задач
 * POST /api/personalization/performance-metrics/calculate
 */
export const calculatePerformanceMetrics = async (
  params?: CalculatePerformanceMetricsRequest
): Promise<UserPerformanceMetrics> => {
  const { data } = await httpClient.post<ApiResponse<UserPerformanceMetrics>>(
    `${BASE_URL}/performance-metrics/calculate${buildQueryString(params || {})}`
  );

  return data.data;
};

/**
 * Получение статистики метрик производительности
 * GET /api/personalization/performance-metrics/statistics
 */
export const getPerformanceMetricsStatistics = async (): Promise<PerformanceMetricsStatisticsResponse> => {
  const { data } = await httpClient.get<ApiResponse<PerformanceMetricsStatisticsResponse>>(
    `${BASE_URL}/performance-metrics/statistics`
  );

  return data.data;
};

// ===== ВРЕМЕННЫЕ ПАТТЕРНЫ =====

/**
 * Анализ временных паттернов
 * POST /api/personalization/temporal-patterns/analyze
 */
export const analyzeTemporalPatterns = async (
  payload: TemporalPatternAnalysisDto
): Promise<TemporalPattern[]> => {
  const { data } = await httpClient.post<ApiResponse<TemporalPattern[]>>(
    `${BASE_URL}/temporal-patterns/analyze`,
    payload
  );

  return data.data;
};

/**
 * Создание временного паттерна
 * POST /api/personalization/temporal-patterns
 */
export const createTemporalPattern = async (
  payload: CreateTemporalPatternDto
): Promise<TemporalPattern> => {
  const { data } = await httpClient.post<ApiResponse<TemporalPattern>>(
    `${BASE_URL}/temporal-patterns`,
    payload
  );

  return data.data;
};

/**
 * Получение активных временных паттернов
 * GET /api/personalization/temporal-patterns/active
 */
export const getActiveTemporalPatterns = async (): Promise<TemporalPattern[]> => {
  const { data } = await httpClient.get<ApiResponse<TemporalPattern[]>>(
    `${BASE_URL}/temporal-patterns/active`
  );

  return data.data;
};

/**
 * Адаптация настроек на основе паттернов
 * POST /api/personalization/temporal-patterns/adapt-preferences
 */
export const adaptPreferences = async (
  payload: AdaptPreferencesRequest
): Promise<void> => {
  await httpClient.post<ApiResponse<void>>(
    `${BASE_URL}/temporal-patterns/adapt-preferences`,
    payload
  );
};

/**
 * Получение рекомендаций на основе временных паттернов
 * GET /api/personalization/temporal-patterns/recommendations
 */
export const getTemporalPatternRecommendations = async (): Promise<TemporalPatternRecommendationsResponse> => {
  const { data } = await httpClient.get<ApiResponse<TemporalPatternRecommendationsResponse>>(
    `${BASE_URL}/temporal-patterns/recommendations`
  );

  return data.data;
};

